import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, effect, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { Subscription } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { SharedMaterialModule } from "@config/shared-material.module";
import { CreateSetupDocumentComponent } from "./create-setup-document/create-setup-document.component";
import { PdfViewEditComponent } from "@components/pdf-editor/pdf-view-edit/pdf-view-edit.component";
import { PdfThankYouComponent } from "@components/pdf-editor/pdf-thank-you/pdf-thank-you.component";
import { PdfSelfSignCompleteComponent } from "@components/pdf-editor/pdf-self-sign-complete/pdf-self-sign-complete.component";
import { IAppToken } from "@modals/api.modal";
import { MatStepper } from "@angular/material/stepper";
import { MatCardModule } from "@angular/material/card";
import { TableService } from "@services/table.service";
import { PdfEditStateService, PdfEditSelectedAnnotation } from "@services/pdf-edit-state.service";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { CreateFlowStateService, CreateWizardStep } from "@services/create-flow-state.service";
import { SignRequestPathDialogComponent } from "./sign-request-path-dialog/sign-request-path-dialog.component";
import { SELF_SIGN_SIGNATURE_COLORS } from "@utils/signature-color.util";
import { TranslateModule } from "@ngx-translate/core";
import { HttpService } from "@services/http.service";
import { Constants } from "@config/constants";
import { SignService } from "@services/sign.service";
import { LoaderComponent } from "@components/layouts/loader/loader.component";
import { StatusMessageComponent } from "@components/layouts/status-message/status-message.component";
import {
  EnumSignerRoleType,
  FIELD_EDITOR_TOOLS,
  isFieldToolAllowedByActions,
  TFieldToolId,
  DEFAULT_SIGNER_ALLOWED_ACTIONS,
  normalizeSignerAllowedActions,
} from "@modals/app.modal";

@Component({
  selector: "app-create-sign-stepper",
  standalone: true,
  imports: [
    SharedMaterialModule,
    CreateSetupDocumentComponent,
    PdfViewEditComponent,
    PdfThankYouComponent,
    PdfSelfSignCompleteComponent,
    MatCardModule,
    TranslateModule,
    LoaderComponent,
    StatusMessageComponent,
  ],
  templateUrl: "./create-sign-stepper.component.html",
  styleUrl: "./create-sign-stepper.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class CreateSignStepperComponent implements OnInit, AfterViewInit, OnDestroy {
  isPdfUploaded = false;
  pdfFile: File | null = null;
  selfSignMode = false;
  selfSignCompleteResult: { signRequestId: string; docName: string } | null = null;
  steps = ["setup Document", "review Document", "sent"];
  topPosition: number = 0;
  appToken!: IAppToken;
  isEditable: boolean = true;
  isDraftRequest = false;
  isLoading = false;
  isError = false;
  private draftDocName = "";
  private draftRequesterDetails: { name?: string; email?: string; phone?: string; requesterId?: string; } = {};


  wizardSetupPhase: 0 | 1 = 0;

  @ViewChild("stepContentContainer") stepContentContainer!: ElementRef;
  @ViewChild("setupDocRef") setupDocRef!: CreateSetupDocumentComponent;
  @ViewChild(PdfViewEditComponent) pdfViewEditComponent!: PdfViewEditComponent;
  @ViewChild("stepper") stepper!: MatStepper;

  pdfEditState = inject(PdfEditStateService);
  private _createFlow = inject(CreateFlowStateService);
  private _dialog = inject(MatDialog);
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _httpService = inject(HttpService);
  private _signService = inject(SignService);
  fontSizes = [8, 10, 12, 14, 16, 18, 20, 24];
  readonly selfSignSignatureColors = SELF_SIGN_SIGNATURE_COLORS;
  readonly fieldTools = FIELD_EDITOR_TOOLS;
  private _cdr = inject(ChangeDetectorRef);
  propertiesDrawerOpen = false;
  private _lastSelectedAnnEl: HTMLElement | null = null;
  private _pinnedPropertyAnnotation: PdfEditSelectedAnnotation | null = null;


  private _returnToDocumentUpload = false;
  private _pathDialogRef?: MatDialogRef<SignRequestPathDialogComponent>;
  private _tableDataSub?: Subscription;

  constructor(public tableService: TableService) {
    effect(() => {
      const ann = this.pdfEditState.selectedAnnotation();
      if (!ann) {
        this._lastSelectedAnnEl = null;
        if (
          this._pinnedPropertyAnnotation &&
          !this._pinnedPropertyAnnotation.element.isConnected
        ) {
          this._pinnedPropertyAnnotation = null;
          this.propertiesDrawerOpen = false;
          this.pdfEditState.setPropertyDrawerOpen(false);
        } else if (!this.propertiesDrawerOpen) {
          this._pinnedPropertyAnnotation = null;
        }
        return;
      }
      if (ann.element !== this._lastSelectedAnnEl) {
        this._lastSelectedAnnEl = ann.element;
        this._pinnedPropertyAnnotation = ann;
        this.propertiesDrawerOpen = true;
        this.pdfEditState.setPropertyDrawerOpen(true);
        this.pdfEditState.schedulePropertyPanelScroll();
      } else {
        this._pinnedPropertyAnnotation = ann;
      }
    });
  }

  isToolDisabled(tool: { id: TFieldToolId; requiresSigner: boolean }): boolean {
    if (tool.requiresSigner && this.isAllReviewers()) {
      return true;
    }
    return !isFieldToolAllowedByActions(tool.id, this.tableService.tableInputData);
  }

  private isAllReviewers(): boolean {
    return (
      this.tableService.tableInputData.length > 0 &&
      this.tableService.tableInputData.every(
        (data) =>
          (data.role ?? "").toString().trim().toLowerCase() ===
          EnumSignerRoleType.REVIEWER.toLowerCase()
      )
    );
  }

  private refreshReviewToolsState(): void {
    const selected = this.pdfEditState.selectedTool();
    if (selected) {
      const tool = this.fieldTools.find((item) => item.id === selected);
      if (tool && this.isToolDisabled(tool)) {
        this.pdfEditState.setSelectedTool(null);
      }
    }
    this._cdr.markForCheck();
  }

  get displayedPropertyAnnotation(): PdfEditSelectedAnnotation | null {
    const live = this.pdfEditState.selectedAnnotation();
    if (live) return live;
    if (this.propertiesDrawerOpen && this._pinnedPropertyAnnotation) {
      const el = this._pinnedPropertyAnnotation.element;
      if (el.isConnected) return this._pinnedPropertyAnnotation;
      this._pinnedPropertyAnnotation = null;
    }
    return null;
  }

  togglePropertiesDrawer(): void {
    this.propertiesDrawerOpen = !this.propertiesDrawerOpen;
    if (this.propertiesDrawerOpen) {
      const ann = this.pdfEditState.selectedAnnotation();
      if (ann) this._pinnedPropertyAnnotation = ann;
    } else {
      this._pinnedPropertyAnnotation = null;
    }
    this.pdfEditState.setPropertyDrawerOpen(this.propertiesDrawerOpen);
  }

  closePropertiesDrawer(): void {
    this.propertiesDrawerOpen = false;
    this._pinnedPropertyAnnotation = null;
    this.pdfEditState.setPropertyDrawerOpen(false);
  }

  ngOnInit(): void {
    this.isDraftRequest = this._activatedRoute.snapshot.data["isDraftRequest"] ?? false;
    this.tableService.replaceTableInputData([]);
    this.pdfEditState.setShowToolsInHeader(false);
    this._createFlow.setWizardFlow("standard");
    this._tableDataSub = this.tableService.tableInputDataChanged$.subscribe(() => {
      if (this.stepper?.selectedIndex === 1) {
        this.refreshReviewToolsState();
      }
    });

    this._createFlow.setWizardStep(0);

    if (this.isDraftRequest) {
      this._createFlow.setSignRequestPath("sendForESignature");
      this.getDraftRequestDetails();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.stepContentContainer) {
        this.topPosition = this.stepContentContainer.nativeElement.offsetTop;
      }

      const pending = this._createFlow.takePendingPdf();
      if (pending && this.setupDocRef) {
        this.setupDocRef.applyPendingPdf(pending);
      }

      this.syncHeaderStep();
      this.openSignRequestPathDialogIfNeeded();
    });
  }

  private openSignRequestPathDialogIfNeeded(): void {
    if (this.isDraftRequest) return;
    if (this._createFlow.signRequestPath()) return;

    const path = (this._router.url || "").split("?")[0].split("#")[0];
    if (path !== "/create/new") return;

    this._pathDialogRef?.close();
    this._pathDialogRef = this._dialog.open(SignRequestPathDialogComponent, {
      width: "700px",
      maxWidth: "95vw",
      minHeight: "440px",
      disableClose: false,
      autoFocus: false,
      panelClass: ["sign-request-path-dialog", "rounded-dialog"],
      backdropClass: "blur-backdrop",
    });

    this._pathDialogRef.afterClosed().subscribe((path) => {
      this._pathDialogRef = undefined;
      if (path) {
        this._createFlow.setSignRequestPath(path);
        return;
      }
      void this._router.navigate(["/requests"]);
    });
  }


  private syncHeaderStep(): void {
    const matIdx = this.stepper?.selectedIndex ?? 0;
    let step: CreateWizardStep = 0;
    if (matIdx === 0) {
      step = this.wizardSetupPhase === 1 ? 1 : 0;
    } else if (matIdx === 1) {
      step = 2;
    } else if (matIdx >= 2) {
      step = 3;
    }
    this._createFlow.setWizardStep(step);
    this.updateFieldStepActions(matIdx);
  }

  private updateFieldStepActions(matIdx: number): void {
    if (matIdx === 1) {
      this._createFlow.registerFieldStepActions({
        back: () => this.previousStepFromField(),
        submit: () => this.submitData(),
      });
    } else {
      this._createFlow.registerFieldStepActions(null);
    }
  }


  onSetupAdvance(): void {
    this.wizardSetupPhase = 1;
    this.syncHeaderStep();
  }

  /** Wired to setup-doc `(wizardSetupGoBack)`: Manage Signer → Document. */
  onSetupGoBack(): void {
    this.wizardSetupPhase = 0;
    this.selfSignMode = false;
    this.syncHeaderStep();
  }

  onSelfSignSelected(): void {
    if (!this.isPdfUploaded || !this.appToken) return;
    this.setupDocRef?.setupSelfSignMode();
    this.selfSignMode = true;
    this.closePropertiesDrawer();
    this.wizardSetupPhase = 0;
    if (this.stepper.selected) this.stepper.selected.completed = true;
    this.stepper.next();
    this.syncHeaderStep();
    this.pdfEditState.setShowToolsInHeader(false);
    this._cdr.detectChanges();
    this._warmupSelfSignPdfEditor();
  }

  private _warmupSelfSignPdfEditor(): void {
    const delays = [0, 120, 320, 560];
    delays.forEach((delay, index) => {
      setTimeout(() => {
        this.pdfViewEditComponent?.ensurePdfRendered(index === 0 || index === delays.length - 1);
        if (index === 0) {
          this.refreshReviewToolsState();
        }
      }, delay);
    });
  }

  handlePdfUploaded(file: File): void {
    this.pdfFile = file;
    this.isPdfUploaded = true;
  }

  private generateFileFromBase64(base64String: string, fileName: string): File {
    const binaryString = window.atob(base64String);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new File([bytes], fileName, { type: "application/pdf" });
  }

  private getDraftRequestDetails(): void {
    this.isLoading = true;
    this.isError = false;

    this._httpService
      .get(
        this._httpService.createEndpoint(
          Constants.API_URI_SIGN_REQUEST_GET_DRAFT,
          undefined,
          { SignRequestId: this._activatedRoute.snapshot.params["requestID"] }
        )
      )
      .subscribe(
        (response: any) => {
          console.log("dkjdkjkjfkjff",response)
          if (response?.Data?.[0]) {
            const responseData = response.Data[0];
            this.draftRequesterDetails = {
              name: responseData.RequesterName,
              email: responseData.RequesterEmail,
              phone: responseData.RequesterPhone,
              requesterId:responseData.RequestNo,
            };
            const docName = `${responseData.DocName ?? "document"}.pdf`;
            this.draftDocName = docName;
            const file = this.generateFileFromBase64(responseData.DocContent, docName);
            this.handlePdfUploaded(file);
            const signers = responseData?.Signers ?? [];
            const tableRows = signers.map((signer: any) => ({
              id: signer.Id,
              name: signer.SignerName,
              email: signer.SignerEmail,
              emailCc:
                signer.emailCC ??
                signer.EmailCC ??
                signer.SignerEmailCc ??
                signer.EmailCc ??
                "",
              phone: signer.Phone,
              role: signer.SignerRole?.toLowerCase(),
              allowedActions: normalizeSignerAllowedActions(
                signer.allowedActions ??
                  signer.AllowedActions ??
                  DEFAULT_SIGNER_ALLOWED_ACTIONS
              ),
              tempID: this._signService.generateUUID(),
            }));
            this.tableService.replaceTableInputData(tableRows);
            setTimeout(() => this.applyDraftToSetup(), 0);
          }
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
          this.isError = true;
        }
      );
  }

  private applyDraftToSetup(): void {
    if (!this.setupDocRef || !this.pdfFile) return;
    this.setupDocRef.applyDraftPrefill(this.draftRequesterDetails, this.pdfFile, this.draftDocName);
  }

  private canProceedFromSetupStep(): boolean {
    return (
      this.isPdfUploaded &&
      (this.isDraftRequest || !!this.appToken) &&
      this.checkForAllValidUsers() &&
      this.setupDocRef.requesterForm.valid
    );
  }

  handleAppTokenUpdated(updatedAppToken: IAppToken): void {
    this.appToken = updatedAppToken;
  }

  resetStepper(): void {
    this.selfSignMode = false;
    this._createFlow.resetWizardStep();
    location.reload();
  }

  get isSelfSignRequestPath(): boolean {
    return this._createFlow.signRequestPath() === "selfSign";
  }

  get resolvedDocumentName(): string {
    return this.setupDocRef?.resolveDocumentName() ?? "";
  }

  private checkForAllValidUsers(): boolean {
    return this.tableService.areAllSignersValid();
  }

  nextStep(): void {
    if (this.canProceedFromSetupStep()) {
      if (this.stepper.selected) this.stepper.selected.completed = true;
      this.stepper.next();
      this.syncHeaderStep();
      this.pdfEditState.setShowToolsInHeader(false);
      setTimeout(() => {
        this.pdfViewEditComponent?.ensurePdfRendered(false);
        this.refreshReviewToolsState();
      }, 100);
    }
  }

  skipTagStep(): void {
    if (this.canProceedFromSetupStep()) {
      if (this.stepper.selected) this.stepper.selected.completed = true;
      this.stepper.next();
      this.syncHeaderStep();
      this.pdfEditState.setShowToolsInHeader(false);
      setTimeout(() => {
        this.pdfViewEditComponent?.ensurePdfRendered(false);
        setTimeout(() => this.submitData(), 200);
      }, 100);
    }
  }

  onStepperSelectionChange(event: StepperSelectionEvent): void {
    const selectedIndex = event.selectedIndex;
    const prevIdx = event.previouslySelectedIndex;

    if (selectedIndex === 0 && prevIdx === 1) {
      if (this._returnToDocumentUpload) {
        this._returnToDocumentUpload = false;
        this.wizardSetupPhase = 0;
      } else {
        this.wizardSetupPhase = 1;
      }
    }

    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      this.syncHeaderStep();
      return;
    }

    if (selectedIndex === 1) {
      const canProceed = this.canProceedFromSetupStep();
      if (!canProceed) {
        setTimeout(() => {
          this.stepper.selectedIndex = prevIdx ?? 0;
          this.nextStep();
        });
        return;
      }
      this.pdfEditState.setShowToolsInHeader(false);
      this.pdfEditState.setSelectedTool(null);
      setTimeout(() => {
        this.pdfViewEditComponent?.ensurePdfRendered(false);
        this.refreshReviewToolsState();
      }, 0);
    } else {
      this.pdfEditState.setShowToolsInHeader(false);
      if (selectedIndex === 0) this.pdfEditState.setSelectedTool(null);
    }

    this.syncHeaderStep();
  }

  previousStep(): void {
    this.previousStepFromField();
  }

  /** Field step → Manage Signer; restores KAIZEN nav sidebar via syncHeaderStep. */
  private previousStepFromField(): void {
    this.pdfEditState.setShowToolsInHeader(false);
    this.pdfEditState.setSelectedTool(null);

    if (this.stepper?.selectedIndex === 1) {
      if (this.selfSignMode) {
        this._returnToDocumentUpload = true;
        this.selfSignMode = false;
        this.wizardSetupPhase = 0;
        this._createFlow.setWizardStep(0);
      } else {
        this.wizardSetupPhase = 1;
        this._createFlow.setWizardStep(1);
      }
    }

    this.stepper.previous();

    // selectionChange may not fire on stepper.previous(); always sync wizard + sidebar state.
    setTimeout(() => this.syncHeaderStep(), 0);
  }

  goToPrevious(): void {
    this.previousStepFromField();
  }

  handleSubmitComplete(): void {
    this.isEditable = false;
    this.pdfEditState.setShowToolsInHeader(false);
    this.pdfEditState.setSelectedTool(null);
    if (this.stepper.selected) this.stepper.selected.completed = true;
    this.stepper.next();
    this.syncHeaderStep();
  }

  handleSelfSignComplete(result: { signRequestId: string; docName: string }): void {
    this.selfSignCompleteResult = result;
    this.handleSubmitComplete();
  }

  submitData(): void {
    let requesterDetails = this.setupDocRef.requesterForm.value;
    if (this.pdfViewEditComponent && requesterDetails) {
      this.pdfViewEditComponent.docName = this.resolvedDocumentName;
      requesterDetails = {
        ...requesterDetails,
        phone: this.tableService.getFormattedNumber(
          requesterDetails.phone,
          this.setupDocRef.countryCode
        ),
      };
      if (this.selfSignMode) {
        this.pdfViewEditComponent.submitSelfSign(requesterDetails);
      } else {
        this.pdfViewEditComponent.submit(requesterDetails);
      }
    }
  }

  ngOnDestroy(): void {
    this._tableDataSub?.unsubscribe();
    this._pathDialogRef?.close();
    this._pathDialogRef = undefined;
    this.pdfEditState.setShowToolsInHeader(false);
    this.pdfEditState.setSelectedTool(null);
    this.pdfEditState.setPropertyDrawerOpen(false);
    this._createFlow.registerFieldStepActions(null);
    this._createFlow.resetWizardStep();
  }
}
